import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useCallback, useMemo, useState, } from 'react';
import { Alert, AlertTitle, Autocomplete, Box, Button, Checkbox, darken, FormControl, FormControlLabel, FormHelperText, FormLabel, Grid, IconButton, Input, InputAdornment, InputLabel, lighten, ListItem, ListItemIcon, ListItemSecondaryAction, ListItemText, MenuItem, OutlinedInput, Radio, RadioGroup, rgbToHex, Select, Slider, Snackbar, TextField, Tooltip, Typography, useRadioGroup, } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker, DateTimePicker } from '@mui/x-date-pickers';
import { pad0, parseFloatWithComma, prettyFloat } from 'webui/utils/number';
import { CheckRounded, FileCopy, RestartAlt } from '@mui/icons-material';
import { any2unix, dayToUnix, inputTime2UnixWithPreserveDate, month2Unix, unixToDay, unixToInputTime, unixToMonth, } from 'webui/utils/unixtime';
import { preventDefault, useBox } from 'shared/hooks/utils';
import { copy2Clipboard } from './utils';
import { plural } from 'webui/utils/plural';
import { ru } from 'date-fns/locale';
import { TwitterPicker } from 'react-color';
import { useHelpPopupWithRef } from 'shared/hooks/ui/help-popup/help-popup-ref';
import { Show } from '../utils';
import { useBuyDesignPackage } from '../../hooks/helpers';
import { LabelWithIcon } from 'console/shared/components/fields/label-with-icon';
import { TextInputWrapper } from 'console/shared/components/fields/text';
import { useBooleanState } from 'shared/hooks/state/boolean';
const marginVariant = 'dense';
export function StrictNumberInput({ editDividedBy = 1, integer, onChange, value, min, max, step, onComplete, ...rest }) {
    const [str, setStr] = useState(null);
    const inputProps = useMemo(() => ({
        min,
        max,
        step,
    }), [min, max, step]);
    const parse = useCallback((str) => {
        let num = parseFloatWithComma(str);
        if (num == null || isNaN(num))
            return null;
        if (integer != null)
            num = ~~num;
        return num;
    }, [integer]);
    const onInputChange = useCallback((event) => {
        const value = event.target.value;
        setStr(value);
        const number = parse(value);
        if (number != null)
            onChange(number * editDividedBy);
    }, [setStr, parse, onChange, editDividedBy]);
    const onBlur = useCallback(() => {
        if (str != null) {
            const value = parse(str);
            if (value != null)
                onChange(value * editDividedBy);
            setStr(null);
        }
    }, [str, parse, onChange, editDividedBy, setStr]);
    const _onComplete = useCallback((e) => {
        if (e.key === 'Enter') {
            onComplete?.(e);
        }
    }, [onComplete]);
    return (_jsx(OutlinedInput, { margin: marginVariant, type: "number", value: str ?? (value != null ? prettyFloat(value / editDividedBy) : ''), onChange: onInputChange, onBlur: onBlur, inputProps: Object.keys(inputProps).length > 0 ? inputProps : undefined, onKeyDown: _onComplete, ...rest }));
}
export function NullableNumberInput(props) {
    const { editDividedBy = 1, integer, onChange, value, min, max, step, onComplete, ...rest } = props;
    const [str, setStr] = useState(null);
    const inputProps = useMemo(() => ({
        min,
        max,
        step,
    }), [min, max, step]);
    const parse = useCallback((str) => {
        let num = parseFloatWithComma(str);
        if (num == null || isNaN(num)) {
            return null;
        }
        if (integer != null) {
            num = ~~num;
        }
        return num;
    }, [integer]);
    const onInputChange = useCallback((event) => {
        const value = event.target.value;
        setStr(value);
        const number = parse(value);
        if (number != null) {
            onChange(number * editDividedBy);
        }
    }, [setStr, parse, onChange, editDividedBy]);
    const onBlur = useCallback(() => {
        if (str != null) {
            const value = parse(str);
            onChange(value == null ? null : value * editDividedBy);
            setStr(null);
        }
    }, [str, parse, onChange, editDividedBy, setStr]);
    const _onComplete = useCallback((e) => {
        if (e.key === 'Enter') {
            onComplete?.(e);
        }
    }, [onComplete]);
    return (_jsx(OutlinedInput, { margin: marginVariant, type: "number", value: str ?? (value != null ? prettyFloat(value / editDividedBy) : ''), onChange: onInputChange, onBlur: onBlur, inputProps: Object.keys(inputProps).length > 0 ? inputProps : undefined, onKeyDown: _onComplete, ...rest }));
}
export function TempstepField(input_props) {
    return (_jsx(StrictNumberInput, { step: 0.1, endAdornment: _jsx(InputAdornment, { position: "end", children: "\u00B0C" }), ...input_props }));
}
export function MoneyInput(props) {
    return (_jsx(StrictNumberInput, { min: 0, step: 1, max: 100_000, ...props, startAdornment: _jsx(InputAdornment, { position: "start", children: "\u20BD" }) }));
}
export function StrictDateInput({ onChange, value, ...rest }) {
    const [date, setDate] = useState(null);
    const onDateChange = useCallback((new_date) => {
        setDate(new_date);
        onChange(any2unix(new_date || 0));
    }, [setDate, onChange]);
    const onBlur = useCallback(() => {
        if (date != null) {
            onChange(any2unix(date));
            setDate(null);
        }
    }, [date, onChange, setDate]);
    return (_jsx(LocalizationProvider, { dateAdapter: AdapterDateFns, adapterLocale: ru, children: _jsx(DatePicker, { inputFormat: "dd.MM.yyyy", value: date ?? (value != null && value != 0 ? new Date(value * 1e3) : null), onChange: onDateChange, renderInput: (params) => (_jsx(TextField, { onBlur: onBlur, ...params, inputProps: {
                    ...params.inputProps,
                    placeholder: 'дд.мм.гггг',
                }, sx: rest.sx })), minDate: "2000.01.01", maxDate: "2030.12.31", readOnly: rest.readOnly }) }));
}
export function DateInput({ onChange, value, ...rest }) {
    const [date, setDate] = useState(null);
    const onDateChange = useCallback((new_date) => {
        setDate(new_date);
        onChange(any2unix(new_date || 0));
    }, [setDate, onChange]);
    const onBlur = useCallback(() => {
        if (date != null) {
            onChange(any2unix(date));
            setDate(null);
        }
    }, [date, onChange, setDate]);
    return (_jsx(LocalizationProvider, { dateAdapter: AdapterDateFns, adapterLocale: ru, children: _jsx(DatePicker, { inputFormat: "dd.MM.yyyy", value: date ?? (value != null && value != 0 ? new Date(value * 1e3) : null), onChange: onDateChange, renderInput: (params) => (_jsx(TextField, { onBlur: onBlur, ...params, inputProps: {
                    ...params.inputProps,
                    placeholder: 'дд.мм.гггг',
                }, sx: rest.sx })), minDate: "2000.01.01", maxDate: "2030.12.31", readOnly: rest.readOnly }) }));
}
export function MaterialDateTimeInput({ onChange, value, ...rest }) {
    const [date, setDate] = useState(null);
    const onDateChange = useCallback((new_date) => {
        setDate(new_date);
        onChange(any2unix(new_date || 0));
    }, [setDate, onChange]);
    const onBlur = useCallback(() => {
        if (date != null) {
            onChange(any2unix(date));
            setDate(null);
        }
    }, [date, onChange, setDate]);
    return (_jsx(LocalizationProvider, { dateAdapter: AdapterDateFns, adapterLocale: ru, children: _jsx(DateTimePicker, { inputFormat: "dd.MM.yyyy HH:mm", 
            //@ts-ignore
            value: date ?? (value != null && value != 0 ? new Date(value * 1e3) : null), onChange: onDateChange, renderInput: (params) => (_jsx(TextField, { onBlur: onBlur, ...params, inputProps: {
                    ...params.inputProps,
                    placeholder: 'дд.мм.гггг чч:мм',
                } })), 
            //@ts-ignore
            minDate: "2000.01.01", 
            //@ts-ignore
            maxDate: "2030.12.31", ...rest }) }));
}
export function StrictTimeInput({ onChange, value, ...rest }) {
    const [str, setStr] = useState(null);
    const onInputChange = useCallback((event) => {
        const input_value = event.target.value;
        onChange(inputTime2UnixWithPreserveDate(input_value, value));
    }, [setStr, onChange]);
    const onBlur = useCallback(() => {
        if (str != null) {
            onChange(inputTime2UnixWithPreserveDate(str, value));
            setStr(null);
        }
    }, [str, onChange, setStr]);
    return (_jsx(OutlinedInput, { margin: marginVariant, type: "time", value: str ?? (value != null ? unixToInputTime(value) : ''), onChange: onInputChange, onBlur: onBlur, ...rest }));
}
export function transformTimeValue(time_el) {
    return pad0(time_el?.hour ?? 0, 2) + ':' + pad0(time_el?.min ?? 0, 2);
}
export function revertTransformTimeValue(val) {
    const _val = val.split(':');
    return { hour: +_val[0], min: +_val[1] };
}
export function TimeFieldInput({ onChange, value, onBlur, onComplete, ...rest }) {
    const [str, setStr] = useState(null);
    const onInputChange = useCallback((event) => {
        const input_value = event.target.value;
        setStr(input_value);
        onChange(revertTransformTimeValue(input_value));
    }, [setStr, onChange]);
    const _onBlur = useCallback((event) => {
        if (str != null) {
            onChange(revertTransformTimeValue(str));
            setStr(null);
            onBlur?.(event);
        }
    }, [str, onChange, setStr, onBlur]);
    return (_jsx(OutlinedInput, { margin: marginVariant, type: "time", value: str ?? transformTimeValue(value), ...rest, onChange: onInputChange, onBlur: _onBlur }));
}
function MaterialAutocompleteImpl(props) {
    const { validation_result, validation_warnings_result, label, help_id, help_options, helper_text, onComplete, onChange, notChosenValue, notChosenTitle, options, value, disabled, disableChoseOptionForSelected, ...rest } = props;
    const has_warning = validation_result == null && validation_warnings_result != null;
    const has_error = validation_result != null;
    const [is_open, _close, _open] = useBooleanState(false);
    const not_chosen = notChosenValue ?? 'not-chosen';
    const boxedOptions = useBox(options);
    const realOptions = useCallback(() => [
        { value: not_chosen, text: notChosenTitle ?? 'Не выбрано' },
        ...boxedOptions.current.flatMap((link_group) => link_group.options.map(({ id, name }) => ({
            value: id != null ? id.toString() : not_chosen,
            text: name,
            group_label: link_group.name,
        }))),
    ], [not_chosen, notChosenTitle]);
    const _onComplete = useCallback((e) => {
        if (e.key === 'Enter') {
            onComplete?.();
        }
    }, [onComplete]);
    const _onChange = useCallback((_, new_item) => {
        if (new_item == null) {
            if (value !== not_chosen) {
                return onChange(null);
            }
            return;
        }
        if (Array.isArray(new_item)) {
            const _new_item = new_item[0];
            if (_new_item.value == null || _new_item.value === not_chosen) {
                return onChange(null);
            }
            return onChange(+_new_item.value);
        }
        if (typeof new_item === 'string') {
            return onChange(+new_item);
        }
        if (new_item.value == null || new_item.value === not_chosen) {
            return onChange(null);
        }
        return onChange(+new_item.value);
    }, [onChange]);
    const is_validation_failed = validation_result != null || validation_warnings_result != null;
    const _onOpen = useCallback(() => {
        if (disableChoseOptionForSelected && value != not_chosen) {
            _close();
        }
        else {
            _open();
        }
    }, [disableChoseOptionForSelected, _open, _close, value, not_chosen]);
    const _onClose = useCallback(() => {
        _close();
    }, [_close]);
    const input_field = useCallback((params) => (_jsxs(FormControl, { fullWidth: true, color: has_warning ? 'warning' : undefined, error: has_error, ref: params.InputProps.ref, focused: is_validation_failed, children: [label != null && (_jsx(FormLabel, { children: _jsx(LabelWithIcon, { has_error: has_error, has_warning: has_warning, label: label, help_id: help_id, help_options: help_options }) })), _jsx(OutlinedInput, { fullWidth: true, margin: marginVariant, inputProps: {
                    ...params.inputProps,
                    autoComplete: 'new-password', // disable autocomplete and autofill
                }, ...params.InputProps, onKeyDown: _onComplete }), is_validation_failed && (_jsx(FormHelperText, { error: has_error, required: has_warning, children: _jsx(_Fragment, { children: validation_result ?? validation_warnings_result }) })), helper_text && _jsx(FormHelperText, { children: helper_text })] })), [
        validation_result,
        validation_warnings_result,
        label,
        help_id,
        help_options,
        helper_text,
        _onComplete,
    ]);
    const _value = realOptions().find((item) => item.value === (value != null ? value.toString() : not_chosen));
    const isOptionEqualToValue = useCallback((option, value) => {
        if (value == null || option == null) {
            return false;
        }
        return option.value == value.value;
    }, []);
    return (_jsx(Autocomplete, { fullWidth: true, isOptionEqualToValue: isOptionEqualToValue, value: _value, options: realOptions(), groupBy: ({ group_label, text }) => group_label ?? text, getOptionLabel: (value) => typeof value == 'string' ? value : 'text' in value ? value.text : value, noOptionsText: "\u041D\u0435 \u043D\u0430\u0448\u043B\u043E\u0441\u044C :(", onChange: _onChange, renderInput: input_field, disabled: disabled, onOpen: _onOpen, onClose: _onClose, open: is_open }));
}
export const MaterialAutocomplete = memo(MaterialAutocompleteImpl);
function MaterialStringAutocompleteImpl(props) {
    const { validation_result, validation_warnings_result, label, help_id, help_options, helper_text, onComplete, onChange, notChosenValue, notChosenTitle, options, value, disabled, ...rest } = props;
    const has_warning = validation_result == null && validation_warnings_result != null;
    const has_error = validation_result != null;
    const not_chosen = notChosenValue ?? 'not-chosen';
    const boxedOptions = useBox(options);
    const realOptions = useCallback(() => [
        { value: not_chosen, text: notChosenTitle ?? 'Не выбрано' },
        ...boxedOptions.current.flatMap((link_group) => link_group.options.map(({ id, name }) => ({
            value: id != null ? id.toString() : not_chosen,
            text: name,
            group_label: link_group.name,
        }))),
    ], [not_chosen, notChosenTitle]);
    const _onComplete = useCallback((e) => {
        if (e.key === 'Enter') {
            onComplete?.();
        }
    }, [onComplete]);
    const _onChange = useCallback((e, new_item) => {
        if (new_item == null) {
            return onChange(null);
        }
        if (Array.isArray(new_item)) {
            const _new_item = new_item[0];
            if (_new_item.value == null || _new_item.value === not_chosen) {
                return onChange(null);
            }
            return onChange(_new_item.value);
        }
        if (typeof new_item === 'string') {
            return onChange(new_item);
        }
        if (new_item.value == null || new_item.value === not_chosen) {
            return onChange(null);
        }
        return onChange(new_item.value);
    }, [onChange]);
    const is_validation_failed = validation_result != null || validation_warnings_result != null;
    const input_field = useCallback((params) => (_jsxs(FormControl, { color: has_warning ? 'warning' : undefined, error: has_error, ref: params.InputProps.ref, focused: is_validation_failed, fullWidth: true, children: [label != null && (_jsx(FormLabel, { children: _jsx(LabelWithIcon, { has_error: has_error, has_warning: has_warning, label: label, help_id: help_id, help_options: help_options }) })), _jsx(OutlinedInput, { margin: marginVariant, inputProps: params.inputProps, ...params.InputProps, onKeyDown: _onComplete }), is_validation_failed && (_jsx(FormHelperText, { error: has_error, required: has_warning, children: _jsx(_Fragment, { children: validation_result ?? validation_warnings_result }) })), helper_text && _jsx(FormHelperText, { children: helper_text })] })), [
        validation_result,
        validation_warnings_result,
        label,
        help_id,
        help_options,
        helper_text,
        _onComplete,
    ]);
    const _value = useMemo(() => realOptions().find((item) => item.value === (value != null ? value.toString() : not_chosen)), [value]);
    return (_jsx(Autocomplete, { fullWidth: true, color: "warning", value: _value, options: realOptions(), groupBy: ({ group_label, text }) => group_label ?? text, getOptionLabel: (value) => typeof value == 'string' ? value : 'text' in value ? value.text : value, noOptionsText: "\u041D\u0435 \u043D\u0430\u0448\u043B\u043E\u0441\u044C :(", onChange: _onChange, renderInput: input_field, disabled: disabled }));
}
export const _MaterialStringAutocomplete = memo(MaterialStringAutocompleteImpl);
export function MaterialStringAutocomplete(props) {
    const { onChange, ...rest } = props;
    const _onChange = useCallback((new_value) => {
        onChange(new_value == null ? '' : new_value);
    }, [onChange]);
    return (_jsx(_MaterialStringAutocomplete, { notChosenTitle: "\u041D\u0435 \u0432\u044B\u0431\u0440\u0430\u043D", notChosenValue: "", validation_result: null, validation_warnings_result: null, help_id: null, ...rest, onChange: _onChange }));
}
function MaterialPhysicalInputOutputSelectionImpl(props) {
    const { onChange, ...rest } = props;
    const _onChange = useCallback((new_value) => {
        onChange(new_value == null ? -1 : new_value);
    }, [onChange]);
    return (_jsx(MaterialAutocomplete, { notChosenTitle: "\u041D\u0435 \u0432\u044B\u0431\u0440\u0430\u043D", notChosenValue: "-1", ...rest, onChange: _onChange }));
}
export const MaterialPhysicalInputOutputSelection = memo(MaterialPhysicalInputOutputSelectionImpl);
function MaterialObjectSelectionImpl(props) {
    return (_jsx(MaterialAutocomplete, { notChosenTitle: "\u041D\u0435 \u0432\u044B\u0431\u0440\u0430\u043D", ...props }));
}
export const MaterialObjectSelection = memo(MaterialObjectSelectionImpl);
function value2str(value) {
    if (value == null)
        return '<null>';
    return value.toString();
}
// function str2value<T extends string | number | null>(str: string): T {
//     if (str == '<null>') return null;
// }
function MaterialDropdownImpl(props) {
    const null_str = '[null]';
    const { onChange, onComplete, disabled, value, label, options, ...rest } = props;
    const onSelectChange = useCallback((ev) => {
        if (ev.target.value == null_str)
            onChange(null);
        else
            onChange(ev.target.value);
    }, [onChange]);
    const _onComplete = useCallback((e) => {
        if (e.key === 'Enter') {
            onComplete?.(e);
        }
    }, [onComplete]);
    return (_jsx(Select, { label: label, value: value?.toString(), onChange: onSelectChange, disabled: !!disabled, margin: marginVariant, ...rest, children: options.map(({ value, text, disabled }, i) => (_jsx(MenuItem, { disabled: disabled, value: value ?? null_str, children: text }, i))) }));
}
export const MaterialDropdown = memo(MaterialDropdownImpl);
export function MaterialDropdownWithGroups(props) {
    const { options, notChosenTitle } = props;
    const null_str = '[null]';
    const onChange = useCallback((ev) => {
        const value = ev.target.value;
        if (notChosenTitle != null && value === notChosenTitle) {
            props.onChange('');
        }
        else if (value == null_str) {
            props.onChange(null);
        }
        else {
            props.onChange(value);
        }
    }, [props.onChange]);
    const obj_with_group = {};
    for (const option of options) {
        if (obj_with_group[option.group] == null)
            obj_with_group[option.group] = [];
        obj_with_group[option.group].push({ value: option.value, text: option.text });
    }
    return (_jsx(Select, { fullWidth: true, margin: marginVariant, label: props.label, value: props.value === '' && notChosenTitle != null
            ? notChosenTitle
            : props.value?.toString(), onChange: onChange, disabled: !!props.disabled, children: Object.entries(obj_with_group).flatMap(([group_name, items], index) => [
            group_name !== 'None' ? (_jsx(MenuItem, { disabled: true, children: group_name }, index)) : null,
            items.map((item, i) => (_jsx(MenuItem, { value: item.value ?? null_str, children: item.text }, index.toString() + i.toString()))),
        ]) }));
}
function TypographyWithCopyImpl(props) {
    const { value, component, error, label, show_copy, row, ...rest } = props;
    const onCopy = useCallback((event) => {
        preventDefault(event);
        copy2Clipboard(value + '');
    }, [value]);
    return (_jsxs(Box, { display: "flex", flexDirection: "row", alignItems: "flex-end", ...rest, children: [_jsxs(Box, { display: "flex", flexDirection: row ? 'row' : 'column', children: [label != null && (_jsx(Box, { mr: row ? 1 : 0, children: _jsx(Typography, { variant: "subtitle1", color: error ? 'error' : 'textSecondary', children: label }) })), component == null ? (_jsx(Typography, { variant: "subtitle1", color: error ? 'error' : 'textPrimary', children: value })) : (_jsx(_Fragment, { children: component }))] }), show_copy && (_jsx(Tooltip, { title: "\u0421\u043A\u043E\u043F\u0438\u0440\u043E\u0432\u0430\u0442\u044C", children: _jsx(IconButton, { onClick: onCopy, sx: {
                        color: 'text.secondary',
                        marginBottom: '-5px',
                    }, size: "large", children: _jsx(FileCopy, { style: { fontSize: 16 } }) }) }))] }));
}
export const TypographyWithCopy = memo(TypographyWithCopyImpl);
function ReadOnlyFieldWithCopyImpl(props) {
    const { label, value, outlined, show_copy, alignTop, success, error, ...rest } = props;
    const [opened, close, open] = useBooleanState(false);
    const onCopy = useCallback((event) => {
        preventDefault(event);
        copy2Clipboard(value + '');
        open();
    }, [value]);
    const endAdornment = useMemo(() => (_jsx(InputAdornment, { position: "start", children: _jsx(Tooltip, { title: "\u0421\u043A\u043E\u043F\u0438\u0440\u043E\u0432\u0430\u0442\u044C", children: _jsx(IconButton, { onClick: onCopy, sx: {
                    color: 'text.secondary',
                }, size: "large", children: _jsx(FileCopy, { style: { fontSize: 16 } }) }) }) })), [onCopy]);
    const style = useMemo(() => ({
        mt: alignTop ?? true ? '-2px !important' : '12px !important',
        ml: alignTop ?? true ? '1px' : undefined,
        fontSize: alignTop ?? true ? undefined : '1.1em',
        color: error ? 'error' : success ? 'success' : undefined,
    }), [alignTop, success, error]);
    return (_jsxs(_Fragment, { children: [outlined ? (_jsx(OutlinedInput, { multiline: true, readOnly: true, value: value, endAdornment: show_copy ? endAdornment : null, margin: marginVariant, sx: style, ...rest })) : (_jsx(Input, { multiline: true, readOnly: true, disableUnderline: true, value: value, endAdornment: show_copy ? endAdornment : null, sx: style, ...rest })), _jsx(Snackbar, { open: opened, autoHideDuration: 1e3, onClose: close, anchorOrigin: { vertical: 'bottom', horizontal: 'center' }, onClick: close, children: _jsx(Alert, { severity: "success", icon: _jsx(CheckRounded, { fontSize: "large" }), style: { alignItems: 'center' }, children: _jsx(AlertTitle, { children: "\u0421\u043A\u043E\u043F\u0438\u0440\u043E\u0432\u0430\u043D\u043E!" }) }) })] }));
}
export const ReadOnlyFieldWithCopy = memo(ReadOnlyFieldWithCopyImpl);
function ReadOnlyListItemWithCopyImpl(props) {
    const { label, value, icon, ...rest } = props;
    const [opened, close, open] = useBooleanState(false);
    const onCopy = useCallback((event) => {
        preventDefault(event);
        copy2Clipboard(value + '');
        open();
    }, [value]);
    return (_jsxs(_Fragment, { children: [_jsxs(ListItem, { ...rest, children: [icon != null && _jsx(ListItemIcon, { children: icon }), _jsx(ListItemText, { primary: label, secondary: value }), _jsx(ListItemSecondaryAction, { children: _jsx(IconButton, { onClick: onCopy, sx: {
                                color: 'text.secondary',
                            }, size: "small", children: _jsx(FileCopy, { style: { fontSize: 16 } }) }) })] }), _jsx(Snackbar, { open: opened, autoHideDuration: 1e3, onClose: close, anchorOrigin: { vertical: 'bottom', horizontal: 'center' }, onClick: close, children: _jsx(Alert, { severity: "success", icon: _jsx(CheckRounded, { fontSize: "large" }), style: { alignItems: 'center' }, children: _jsx(AlertTitle, { children: "\u0421\u043A\u043E\u043F\u0438\u0440\u043E\u0432\u0430\u043D\u043E!" }) }) })] }));
}
export const ReadOnlyListItemWithCopy = memo(ReadOnlyListItemWithCopyImpl);
function MaterialReadOnlyImpl(props) {
    const { label, value, help_text, alignTop, success, error, ...rest } = props;
    return (_jsxs(FormControl, { fullWidth: true, variant: "outlined", children: [_jsx(FormLabel, { children: _jsx(LabelWithIcon, { has_error: false, has_warning: false, ...props }) }), _jsx(ReadOnlyFieldWithCopy, { value: value, ...rest }), help_text != null && _jsx(FormHelperText, { children: help_text })] }));
}
export const MaterialReadOnly = memo(MaterialReadOnlyImpl);
export const MaterialBoolControl = memo(function MaterialBoolControl(props) {
    const { value, Control = Checkbox, onChange, help_id, label, custom_label, stopPropagation = false, onComplete, ...rest } = props;
    const onChangeCheckbox = useCallback((e, checked) => {
        e.stopPropagation();
        //not use preventDefault --> need 2 click
        onChange(checked);
    }, [value, onChange]);
    const control_label = useMemo(() => {
        if (label != null) {
            return (_jsx(LabelWithIcon, { has_error: false, has_warning: false, help_id: help_id ?? null, label: label, ...props }));
        }
        if (custom_label != null) {
            return custom_label;
        }
        return null;
    }, [custom_label, label, help_id, props]);
    return (_jsx(FormControlLabel, { control: _jsx(Control, { checked: value, onChange: onChangeCheckbox, onClick: stopPropagation ? (e) => e.stopPropagation() : undefined, name: label, color: "primary", ...rest }), label: control_label }));
});
export const MaterialRadioGroupControl = memo(function MaterialRadioGroupControl(props) {
    const { value, onChange, main_label, control_values, color, onComplete, ...rest } = props;
    const onChangeGroup = useCallback((_event, value) => {
        //not use preventDefault --> need 2 click
        onChange(value);
    }, [onChange]);
    return (_jsxs(FormControl, { component: "fieldset", children: [main_label != null && (_jsx(FormLabel, { component: "legend", children: main_label })), _jsx(RadioGroup, { value: value, onChange: onChangeGroup, ...rest, children: control_values
                    .filter(({ hide }) => !hide)
                    .map(({ label, help_id, value }) => (_jsx(FormControlLabel, { value: value, control: _jsx(Radio, { color: color ?? 'primary' }), label: _jsx(LabelWithIcon, { has_error: false, has_warning: false, label: label, help_id: help_id }) }, label + `${value}`))) })] }));
});
export const CustomRadioButton = memo(function CustomRadioButton(props) {
    const { children } = props;
    const radioGroup = useRadioGroup();
    const checked = radioGroup?.value === props.value;
    return (_jsx(FormControlLabel, { control: _jsx(Radio, {}), label: children, checked: checked, slotProps: {
            typography: {
                sx: checked
                    ? {}
                    : {
                        opacity: 0.5,
                    },
            },
        }, ...props }));
});
function RangeFieldImpl(props) {
    const { min_field, minCaption, minCaptionEnd, max_field, maxCaption, maxCaptionEnd, helper_text, min, max, step, editDividedBy, } = props;
    const has_warning = min_field.validation_result == null &&
        max_field.validation_result == null &&
        (min_field.validation_warnings_result != null ||
            max_field.validation_warnings_result != null);
    const warning_text = has_warning
        ? min_field.validation_warnings_result != null
            ? minCaption + ' диапазон: ' + min_field.validation_warnings_result
            : max_field.validation_warnings_result != null &&
                maxCaption + ' диапазон: ' + max_field.validation_warnings_result
        : null;
    const has_error = min_field.validation_result != null || max_field.validation_result != null;
    const error_text = has_error
        ? min_field.validation_result != null
            ? minCaption + ' диапазон: ' + min_field.validation_result
            : max_field.validation_result != null &&
                maxCaption + ' диапазон: ' + max_field.validation_result
        : null;
    const is_validation_failed = warning_text || error_text;
    return (_jsxs(Grid, { container: true, alignItems: "center", sx: {
            pl: 1,
        }, color: has_warning ? 'warning' : undefined, children: [_jsx(Grid, { item: true, xs: 12, children: _jsx(InputLabel, { error: has_error, children: _jsx(LabelWithIcon, { has_error: has_error, has_warning: has_warning, ...props }) }) }), _jsx(Grid, { item: true, xs: 12, children: _jsxs(Box, { display: "flex", alignItems: "center", children: [_jsx(Box, { mr: 1, children: _jsx(StrictNumberInput, { ...min_field, min: min, max: max, error: has_error, step: step, editDividedBy: editDividedBy, color: has_warning ? 'warning' : undefined, startAdornment: !minCaptionEnd ? (_jsx(InputAdornment, { position: "start", children: minCaption })) : null, endAdornment: minCaptionEnd ? (_jsx(InputAdornment, { position: "end", children: minCaption })) : null }) }), _jsx(StrictNumberInput, { ...max_field, min: min, max: max, color: has_warning ? 'warning' : undefined, step: step, error: has_error, editDividedBy: editDividedBy, startAdornment: !maxCaptionEnd ? (_jsx(InputAdornment, { position: "start", children: maxCaption })) : null, endAdornment: maxCaptionEnd ? (_jsx(InputAdornment, { position: "end", children: maxCaption })) : null })] }) }), is_validation_failed && (_jsx(Grid, { item: true, xs: 12, children: _jsx(FormHelperText, { error: has_error, required: has_warning, children: warning_text ?? error_text }) })), helper_text && (_jsx(Grid, { item: true, xs: 12, children: _jsx(FormHelperText, { children: helper_text }) }))] }));
}
export const RangeField = memo(RangeFieldImpl);
function NullableRangeFieldImpl(props) {
    const { min_field, minCaption, minCaptionEnd, max_field, maxCaption, maxCaptionEnd, helper_text, min, max, step, editDividedBy, } = props;
    const has_warning = min_field.validation_result == null &&
        max_field.validation_result == null &&
        (min_field.validation_warnings_result != null ||
            max_field.validation_warnings_result != null);
    const warning_text = min_field.validation_result === max_field.validation_result
        ? min_field.validation_result
        : has_warning
            ? min_field.validation_warnings_result != null
                ? minCaption + ' диапазон: ' + min_field.validation_warnings_result
                : max_field.validation_warnings_result != null &&
                    maxCaption + ' диапазон: ' + max_field.validation_warnings_result
            : null;
    const has_error = min_field.validation_result != null || max_field.validation_result != null;
    const error_text = min_field.validation_result === max_field.validation_result
        ? min_field.validation_result
        : has_error
            ? min_field.validation_result != null
                ? minCaption + ' диапазон: ' + min_field.validation_result
                : max_field.validation_result != null &&
                    maxCaption + ' диапазон: ' + max_field.validation_result
            : null;
    const is_validation_failed = warning_text || error_text;
    return (_jsxs(Grid, { container: true, alignItems: "center", color: has_warning ? 'warning' : undefined, children: [_jsx(Grid, { item: true, xs: 12, children: _jsx(InputLabel, { error: has_error, children: _jsx(LabelWithIcon, { has_error: has_error, has_warning: has_warning, ...props }) }) }), _jsx(Grid, { item: true, xs: 12, children: _jsxs(Box, { display: "flex", alignItems: "center", children: [_jsx(Box, { mr: 1, children: _jsx(NullableNumberInput, { ...min_field, value: min_field.value == null ||
                                    min_field.value === ''
                                    ? null
                                    : +min_field.value, min: min, max: max, step: step, color: has_error ? 'warning' : undefined, error: has_error, editDividedBy: editDividedBy, startAdornment: !minCaptionEnd ? (_jsx(InputAdornment, { position: "start", children: minCaption })) : null, endAdornment: minCaptionEnd ? (_jsx(InputAdornment, { position: "end", children: minCaption })) : null }) }), _jsx(NullableNumberInput, { ...max_field, value: max_field.value == null ||
                                max_field.value === ''
                                ? null
                                : +max_field.value, min: min, max: max, step: step, error: has_error, color: has_warning ? 'warning' : undefined, editDividedBy: editDividedBy, startAdornment: !maxCaptionEnd ? (_jsx(InputAdornment, { position: "start", children: maxCaption })) : null, endAdornment: maxCaptionEnd ? (_jsx(InputAdornment, { position: "end", children: maxCaption })) : null })] }) }), is_validation_failed && (_jsx(Grid, { item: true, xs: 12, children: _jsx(FormHelperText, { error: has_error, required: has_warning, children: warning_text ?? error_text }) })), helper_text && (_jsx(Grid, { item: true, xs: 12, children: _jsx(FormHelperText, { children: helper_text }) }))] }));
}
export const NullableRangeField = memo(NullableRangeFieldImpl);
export function MonthField(props) {
    const _onChange = useCallback((value) => {
        props.onChange(month2Unix(value));
    }, [props.onChange]);
    return (_jsx(StrictNumberInput, { min: 0, max: 60, step: 1, endAdornment: _jsx(InputAdornment, { position: "end", children: "\u043C\u0435\u0441" }), ...props, value: unixToMonth(props.value), onChange: _onChange }));
}
export function DayField(props) {
    const _value = useMemo(() => unixToDay(props.value), [props.value]);
    const _onChange = useCallback((value) => {
        props.onChange(dayToUnix(value));
    }, [props.onChange]);
    return (_jsx(StrictNumberInput, { min: 0, max: 365, step: 1, endAdornment: _jsx(InputAdornment, { position: "end", children: plural(_value, 'день', 'дня', 'дней') }), ...props, value: _value, onChange: _onChange }));
}
export function withPartialProps(Component, partial_props) {
    function wrapper(rest) {
        return _jsx(Component, { ...partial_props, ...rest });
    }
    wrapper.dispayName = `withPartialProps<${Component.displayName}>`;
    return wrapper;
}
export const AutoFocusTextInputWrapper = withPartialProps(TextInputWrapper, {
    autoFocus: true,
});
const common_colors = [
    '#ff3535',
    '#89ff65',
    '#35FFD0',
    '#355dff',
    '#FFC935',
    '#9635ff',
    '#fb35ff',
    '#E2E2E2',
];
const colors = common_colors
    .flatMap((color) => [
    lighten(color, 0.9),
    lighten(color, 0.7),
    lighten(color, 0.5),
    lighten(color, 0.2),
    color,
    darken(color, 0.2),
    darken(color, 0.4),
    darken(color, 0.6),
])
    .map((color) => rgbToHex(color));
export const MaterialColorPicker = memo(function MaterialColorPicker(props) {
    const { value, label, default_palette, allow_design_package, onChange } = props;
    const [HelpPopup, ref, open, close] = useHelpPopupWithRef();
    const BuyDesignPackage = useBuyDesignPackage(close);
    const default_color = useMemo(() => {
        if (default_palette) {
            if ('main' in default_palette) {
                return default_palette.main;
            }
            if ('default' in default_palette) {
                return default_palette.default;
            }
        }
        return value ?? '';
    }, [default_palette]);
    const onColorReset = useCallback(() => {
        if (default_color) {
            onChange(default_color);
        }
    }, [default_color, onChange]);
    const onColorChange = useCallback(({ hex }) => {
        onChange(hex);
    }, [onChange]);
    return (_jsxs(Box, { display: "grid", gridTemplateColumns: "1fr 50px 25px", columnGap: 1, alignItems: "center", ref: ref, children: [_jsx(Typography, { children: label }), _jsxs(Box, { display: "flex", columnGap: 1, children: [_jsx(IconButton, { onClick: open, children: _jsx(Box, { width: 25, height: 15, borderRadius: 1, bgcolor: value ?? default_color, border: (theme) => `1px solid ${theme.palette.action.active}` }) }), _jsx(IconButton, { onClick: onColorReset, children: _jsx(RestartAlt, {}) })] }), _jsx(HelpPopup, { placement: "bottom-end", styleProps: { p: 0 }, footer: _jsx(_Fragment, {}), children: allow_design_package ? (_jsxs(Box, { display: "flex", flexDirection: "column", gap: 2, children: [_jsx(TwitterPicker, { triangle: "hide", color: value ?? default_color, colors: colors, onChange: onColorChange, onChangeComplete: onColorChange, width: "320px" }), _jsx(Button, { onClick: close, variant: "contained", fullWidth: true, children: "\u041F\u0440\u0438\u043C\u0435\u043D\u0438\u0442\u044C" })] })) : (BuyDesignPackage) })] }));
});
export const MaterialSlider = memo(function MaterialSlider(props) {
    const { label, value_view, ...rest } = props;
    return (_jsxs(Box, { display: "flex", width: "100%", alignItems: "center", columnGap: 2, children: [_jsxs(FormControl, { fullWidth: true, children: [_jsx(Show, { when: label, children: _jsx(FormLabel, { children: label }) }), _jsx(Slider, { ...rest })] }), value_view] }));
});
